import {
  resolveThresholdBandColor,
  resolveThresholdPointColor,
} from "@/components/graph/threshold-colors";

import type { ThresholdGaugeSegments } from "./time-series-thresholds-rows";
import styles from "./time-series-thresholds-gauge.module.css";

interface TimeSeriesThresholdsGaugeProps {
  segments: ThresholdGaugeSegments;
  /** Unit symbol appended to the value labels for context. */
  unitSymbol?: string;
}

/**
 * Schematic vertical gauge that visualizes the active thresholds: colored regions
 * (light tints) stacked by value, a neutral band for the unaffected middle, and
 * solid boundary lines in the saturated threshold color with an overlaid value
 * label.
 *
 * Purely a visual aid (the rows carry the editable data), so it is hidden from
 * assistive tech.
 */
export function TimeSeriesThresholdsGauge({
  segments,
  unitSymbol,
}: TimeSeriesThresholdsGaugeProps) {
  const { bands, lines, zero } = segments;

  return (
    <div className={styles.gauge} aria-hidden>
      <div className={styles.track}>
        {bands.map((band) => (
          <div
            key={band.key}
            className={styles.band}
            style={{
              flexGrow: band.weight,
              background: band.colorKey
                ? resolveThresholdBandColor(band.colorKey)
                : undefined,
            }}
          />
        ))}
      </div>
      {zero && (
        <div className={styles.line} style={{ top: `${zero.position * 100}%` }}>
          <span className={styles.zeroRule} />
          <span className={styles.zeroLabel}>0{unitSymbol ?? ""}</span>
        </div>
      )}
      {lines.map((line) => {
        const color = resolveThresholdPointColor(line.colorKey);
        return (
          <div
            key={line.key}
            className={styles.line}
            style={{ top: `${line.position * 100}%` }}
          >
            <span className={styles.lineRule} style={{ backgroundColor: color }} />
            <span className={styles.lineLabel} style={{ color }}>
              {formatValue(line.value)}
              {unitSymbol ?? ""}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Trim float noise from user-entered values without forcing a fixed precision. */
function formatValue(value: number): string {
  return String(Math.round(value * 1000) / 1000);
}
